// CAT-1/CAT-4 — catalog search for binding a canned LABOR line to a
// canonical jobs_catalog row. Returns just the fields the line resolver
// (resolveCannedLineMetadata) needs to preview + persist a binding.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import type { CatalogMetaRow } from '@/lib/cannedJobs/resolveCannedLineMetadata';

export interface CatalogSearchRow extends CatalogMetaRow {
  label: string;
  /** Present on the by-code LOOKUP only: a retired binding still resolves. */
  retired_at?: string | null;
  superseded_by_code?: string | null;
}

export function useCatalogJobSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['canned-catalog-search', q],
    enabled: q.length >= 2,
    staleTime: 60_000,
    queryFn: async (): Promise<CatalogSearchRow[]> => {
      const pattern = `%${q}%`;
      // NEW selection (binding a line): retired catalog rows are never offered.
      const { data, error } = await newJobCatalogQuery('code, label, category_key, sub_menu, skill_level, minutes_default, primary_bay_type')
        .or(`label.ilike.${pattern},code.ilike.${pattern}`)
        .order('label', { ascending: true })
        .limit(25);
      if (error) throw error;
      return (data ?? []) as CatalogSearchRow[];
    },
  });
}

/** Resolve a single catalog row by exact code (for rehydrating a saved binding). */
export function useCatalogJobByCode(code: string | null | undefined) {
  return useQuery({
    queryKey: ['canned-catalog-by-code', code],
    enabled: !!code,
    staleTime: 60_000,
    queryFn: async (): Promise<CatalogSearchRow | null> => {
      // LOOKUP: a saved binding keeps resolving even after its row is retired;
      // retired_at / superseded_by_code let the editor badge it.
      const { data, error } = await (supabase as any)
        .from('jobs_catalog')
        .select('code, label, category_key, sub_menu, skill_level, minutes_default, primary_bay_type, retired_at, superseded_by_code')
        .eq('code', code)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as CatalogSearchRow | null;
    },
  });
}
